import Input from "../../../components/Input/Input"
import React, { useEffect, useRef, useState, useCallback } from "react"
import Button from "../../../components/Button/Button"
import { generateSSNumber, hasSsnOnFile, omitSsnUpdate, phoneNumber, ssnForPrefill } from "../../../utils/payment"
import { selectFeatureFlag } from "../../../features/featureFlags/selectors"
import { clearListCache } from "../../../utils/listCache"
import { emailBlocksSave } from "./emailOptional"
import { leadSourceChoices } from "./officeLeadSources"
import { showSnackBar } from "../../../components/Snackbar/Snackbar"
import { createCustomUser, editContactById, getContactById } from "../../../features/customer/customerSlice"
import { useDispatch, useSelector } from "react-redux"
import Calendar from "../../../components/Calendar/Calendar"
import { useMyContextLoading } from "../../../components/LoadingContextProvider/LoadingContextProvider"
import { useParams } from "react-router-dom"
import { GOOGLE_MAPS_API_KEY } from "../../../config/consts"
import { usePlacesWidget } from "react-google-autocomplete"
import { fetchStates } from "../../../features/states/states"
import Dropdown from "../../../components/Dropdown/Dropdown"
import { fetchTimeZones } from "../../../features/timeZone/timeZone"
import { StorageGetItem, USER } from "../../../lib/storage"
import { ACQUISITION_SOURCE_OPTIONS, LEGACY_LEAD_SOURCE_MAX_LENGTH } from "./consts"
import { Autocomplete, TextField } from "@mui/material"
import { dateToServer, serverToLocal, serverToLocalDateOnly } from "../../../utils/date"
import Switch from "../../../components/Switch/Switch"
import { createCoApplicant, getCoApplicantById, updateCoApplicant } from "../../../features/coApplicants/coApplicants"
import { createContactLink, getContactLinksByUserId } from "../../../features/contactLinks/contactLinks"
import CoApplicantSearch from "../../../components/CoApplicantSearch/CoApplicantSearch"
import usePinnedFeatureFlag from "../../../hooks/usePinnedFeatureFlag"
import { CREDIT_PULL_SSN_GUARDS } from "../../../config/featureFlagKeys"

const CoApplicantAddressFields = ({ coApplicantInfo, handleCoApplicantInputChange, setCoApplicantInfo, states }) => {
  const [coAddressInput, setCoAddressInput] = useState('')

  const handleCoAddressSelect = useCallback((place) => {
    // Same guard as the primary handler (FE-P4): Google's widget can return a place with
    // no address_components — optional-chain every .find() so selection can't crash.
    const streetNumber = place?.address_components?.find(c => c.types.includes("street_number"))?.long_name || ""
    const route = place?.address_components?.find(c => c.types.includes("route"))?.long_name || ""
    const fullAddress = streetNumber ? `${streetNumber} ${route}` : route

    setCoApplicantInfo(prev => ({
      ...prev,
      address: fullAddress,
      city: place?.address_components?.find(c => c.types.includes("locality"))?.long_name || "",
      state: place?.address_components?.find(c => c.types.includes("administrative_area_level_1"))?.short_name || "",
      zip: place?.address_components?.find(c => c.types.includes("postal_code"))?.long_name || "",
    }))
    setCoAddressInput(place?.formatted_address || "")
  }, [])

  const { ref: coAddressRef } = usePlacesWidget({
    apiKey: GOOGLE_MAPS_API_KEY,
    onPlaceSelected: handleCoAddressSelect,
    options: {
      types: ['address'],
      componentRestrictions: { country: 'us' },
    },
  })

  return (
    <>
      <div className="flex px-12 pt-2 gap-4">
        <div className="flex-1">
          <Input
            inputRef={coAddressRef}
            placeholder="Enter Address"
            label="Address"
            value={coAddressInput}
            onChange={(e) => {
              setCoAddressInput(e.target.value)
              setCoApplicantInfo(prev => ({ ...prev, address: e.target.value }))
            }}
          />
        </div>
        <div className="flex-1">
          <Input
            placeholder="Suite/Apt"
            label="Suite/Apt"
            value={coApplicantInfo.aptNumber}
            onChange={(e) => handleCoApplicantInputChange({ name: 'aptNumber', value: e.target.value })}
          />
        </div>
      </div>
      <div className="flex px-12 py-6 gap-4">
        <div className="flex-1">
          <Input
            placeholder="Enter City"
            label="City"
            value={coApplicantInfo.city}
            onChange={(e) => handleCoApplicantInputChange({ name: 'city', value: e.target.value })}
          />
        </div>
        <div className="flex-1">
          <Dropdown
            menuItems={states?.map((state) => ({
              label: `${state.shortName} - ${state.fullName}`,
              value: state.shortName,
            }))}
            label="State"
            placeholder="Enter State"
            value={coApplicantInfo.state}
            type="text"
            name="state"
            className="form-control"
            setValue={(e) => handleCoApplicantInputChange({ name: 'state', value: e })}
          />
        </div>
      </div>
      <div className="flex px-12 pb-6">
        <div className="w-1/2">
          <Input
            placeholder="Enter Zip"
            label="Zip"
            value={coApplicantInfo.zip}
            onChange={(e) => {
              const value = e.target.value?.replace(/\D/g, '')
              handleCoApplicantInputChange({ name: 'zip', value })
            }}
          />
        </div>
      </div>
    </>
  )
}

export const BasicInfo = ({
  userInfo,
  setUserInfo,
  userId,
  setUserId,
  setDisableTabs,
  setSelectedTab,
  setSsnNumber,
  setHasSsnOnFile,
  getNewLeadDetails,
  gatesOnLeadDetails = false,
  leadSourceMaxLength = LEGACY_LEAD_SOURCE_MAX_LENGTH,
  isCreate,
  isBillingAccount,
  checkUserBankAccountAndSigningInfo,
  skipQCCall,
  hasCoApplicant,
  setHasCoApplicant,
  coApplicantInfo,
  setCoApplicantInfo,
  coApplicantId,
  setCoApplicantId,
  emailOptional = false,
  leadSourceOptions = null,
}) => {

  const { triggerLoading } = useMyContextLoading()
  const dispatch = useDispatch()
  let { userId: userIdUrl } = useParams()
  const [reqError, setReqError] = useState('')
  const [addressInput, setAddressInput] = useState('')
  const [states, setStates] = useState([])
  const [timeZones, setTimeZone] = useState([])
  const [userRole, setUserRole] = useState('')
  const [userCompany, setUserCompany] = useState('')
  const ssnWriteGuard = useSelector(selectFeatureFlag('CONTACT_SSN_WRITE_GUARD'))
  const ssnWriteGuardRef = useRef(ssnWriteGuard)
  const coAppSsnWithheldRef = useRef(false)
  const { enabled: keepIdOnRefusal, pin: pinKeepIdOnRefusal } = usePinnedFeatureFlag(CREDIT_PULL_SSN_GUARDS)
  const [sameAddress, setSameAddress] = useState(false)
  const [coApplicantMode, setCoApplicantMode] = useState('create')
  const [coApplicantLinked, setCoApplicantLinked] = useState(false)
  const [linkedCoApplicantId, setLinkedCoApplicantId] = useState(null)

  const handleCoApplicantInputChange = ({ name, value }) => {
    switch (name) {
      case 'primaryPhone':
        setCoApplicantInfo((prev) => ({ ...prev, [name]: phoneNumber(value) }))
        break
      case 'firstName':
      case 'lastName':
      case 'middleName':
        const coLettersOnly = value?.replace(/[^a-zA-Z']/g, '')
        setCoApplicantInfo((prev) => ({ ...prev, [name]: coLettersOnly }))
        break
      default:
        setCoApplicantInfo((prev) => ({ ...prev, [name]: value }))
    }
  }

  const handleSelectExistingCoApplicant = (coApplicant) => {
    setLinkedCoApplicantId(coApplicant._id)
    coAppSsnWithheldRef.current = hasSsnOnFile(coApplicant.ssNumber) && !ssnForPrefill(coApplicant.ssNumber)
    setCoApplicantInfo({
      firstName: coApplicant.firstName || '',
      lastName: coApplicant.lastName || '',
      middleName: coApplicant.middleName || '',
      email: coApplicant.email || '',
      primaryPhone: phoneNumber(coApplicant.primaryPhone) || '',
      ssNumber: ssnForPrefill(coApplicant.ssNumber),
      dateOfBirth: coApplicant.dateOfBirth ? serverToLocalDateOnly(coApplicant.dateOfBirth) : '',
      gender: coApplicant.gender || '',
      address: coApplicant.address?.address || '',
      aptNumber: coApplicant.address?.aptNumber || '',
      city: coApplicant.address?.city || '',
      state: coApplicant.address?.state || '',
      zip: coApplicant.address?.zip || '',
    })
  }

  useEffect(() => {
    StorageGetItem(USER).then(result => {
      setUserRole(result?.role)
      setUserCompany(result?.company)
    })
  }, [])

  useEffect(() => {
    const handleReqError = () => {
      reqError.length > 0 && showSnackBar("error", reqError)
      setReqError('')
    }
    handleReqError()
  }, [reqError])

  const handleInputChange = ({ name, value }) => {
    switch (name) {
      case 'primaryPhone':
      case 'secondaryPhone':
        setUserInfo((prev) => ({ ...prev, [name]: phoneNumber(value) }))
        break
      case 'firstName':
      case 'lastName':
      case 'middleName':
        const lettersOnly = value?.replace(/[^a-zA-Z']/g, '')
        setUserInfo((prev) => ({ ...prev, [name]: lettersOnly }))
        break
      default:
        setUserInfo((prev) => ({ ...prev, [name]: value }))
    }
  }


  const getUserById = async () => {
    triggerLoading(true)
    try {
      const response = await dispatch(getContactById(userIdUrl))
      if (response?.payload?.data) {

        const {
          _id,
          firstName,
          lastName,
          middleName,
          primaryPhone,
          secondaryEmail,
          dateOfBirth,
          enrollmentDate,
          address,
          gender,
          secondaryPhone,
          timeZone,
          ssNumber,
          acquisitionSource,
        } = response?.payload?.data
        const ssn = ssnForPrefill(ssNumber)
        setSsnNumber(ssn)
        setHasSsnOnFile(hasSsnOnFile(ssNumber))
        setUserId(_id)

        setUserInfo({
          firstName: firstName || "",
          lastName: lastName || "",
          primaryPhone: phoneNumber(primaryPhone) || "",
          email: secondaryEmail || "",
          address: address?.address || "",
          aptNumber: address?.aptNumber || "",
          city: address?.city || "",
          state: address?.state || "",
          zip: address?.zip || "",
          dateOfBirth: serverToLocalDateOnly(dateOfBirth) || "",
          enrollmentDate: serverToLocalDateOnly(enrollmentDate) || "",
          middleName: middleName || "",
          gender: gender || "",
          secondaryPhone: phoneNumber(secondaryPhone) || "",
          timeZone: timeZone || "",
          acquisitionSource: acquisitionSource || "",
        })
        if (address?.address) {
          const fullAddress = `${address.address}, ${address.city}, ${address.state}`
          setAddressInput(fullAddress)
        }
        if (!isBillingAccount) {
          setDisableTabs(prev => ({
            ...prev,
            creditSelect: false
          }))
        }
        checkUserBankAccountAndSigningInfo({ custUserId: _id })

        const linksRes = await dispatch(getContactLinksByUserId(_id))
        if (Array.isArray(linksRes?.payload) && linksRes.payload.length > 0) {
          const coLink = linksRes.payload.find(l => l.linkType === 'co-applicant')
          if (coLink) {
            const coApplicantRef = coLink.primaryContact?._id === _id ? coLink.linkedContact : coLink.primaryContact
            if (coApplicantRef?._id) {
              const fullCoRes = await dispatch(getCoApplicantById(coApplicantRef._id))
              const coApplicant = fullCoRes?.payload?.data || fullCoRes?.payload || coApplicantRef
              setHasCoApplicant(true)
              setCoApplicantId(coApplicantRef._id)
              setCoApplicantLinked(true)
              setCoApplicantMode('edit')
              setLinkedCoApplicantId(coApplicantRef._id)
              coAppSsnWithheldRef.current = hasSsnOnFile(coApplicant.ssNumber) && !ssnForPrefill(coApplicant.ssNumber)
              setCoApplicantInfo({
                firstName: coApplicant.firstName || '',
                lastName: coApplicant.lastName || '',
                middleName: coApplicant.middleName || '',
                email: coApplicant.email || '',
                primaryPhone: phoneNumber(coApplicant.primaryPhone) || '',
                ssNumber: ssnForPrefill(coApplicant.ssNumber),
                dateOfBirth: coApplicant.dateOfBirth ? serverToLocalDateOnly(coApplicant.dateOfBirth) : '',
                gender: coApplicant.gender || '',
                address: coApplicant.address?.address || '',
                aptNumber: coApplicant.address?.aptNumber || '',
                city: coApplicant.address?.city || '',
                state: coApplicant.address?.state || '',
                zip: coApplicant.address?.zip || '',
              })
            }
          }
        }
      }
    } catch (error) {
      setReqError(error.message || 'Failed to get customer data')
    }
    triggerLoading(false)
  }


  const handleCreateContact = async () => {
    triggerLoading(true)
    let coApplicantFailed = false
    const newNumber = userInfo?.primaryPhone?.replace(/\D/g, "")
    const newSecondaryNumber = userInfo?.secondaryPhone?.replace(/\D/g, "")

    try {
      const userData = {
        ...userInfo,
        primaryPhone: newNumber,
        secondaryPhone: newSecondaryNumber,
        dateOfBirth: dateToServer(userInfo.dateOfBirth),
        enrollmentDate: dateToServer(userInfo.enrollmentDate),
      }

      if (skipQCCall) {
        userData.qcCallStatus = 'skipped'
      }
      // WS #2219705: a PACK client with no email is created without one, not with an empty string.
      if (emailOptional && !userInfo?.email) delete userData.email

      const response = await dispatch(createCustomUser(userData))

      if (response.error) {
        setReqError(response.payload ?? '')
      } else {
        const newUserId = response?.payload?.data?._id
        if (newUserId) {
          clearListCache('leads')
          clearListCache('customers')
          setUserId(newUserId)

          if (hasCoApplicant) {
            if (coApplicantMode === 'link' && linkedCoApplicantId) {
              setCoApplicantId(linkedCoApplicantId)
              const linkResponse = await dispatch(createContactLink({
                primaryContact: newUserId,
                linkedContact: linkedCoApplicantId,
                linkType: 'co-applicant',
                company: response?.payload?.data?.company,
              }))
              if (linkResponse.error) {
                setReqError(linkResponse.payload || 'Failed to link co-applicant')
                coApplicantFailed = true
              } else {
                setCoApplicantLinked(true)
              }
            } else {
              const coApplicantData = {
                firstName: coApplicantInfo.firstName,
                lastName: coApplicantInfo.lastName,
                email: coApplicantInfo.email,
                primaryPhone: coApplicantInfo.primaryPhone?.replace(/\D/g, ''),
                ssNumber: coApplicantInfo.ssNumber?.replace(/\D/g, ''),
                dateOfBirth: dateToServer(coApplicantInfo.dateOfBirth),
                middleName: coApplicantInfo.middleName,
                gender: coApplicantInfo.gender,
                address: {
                  address: sameAddress ? userInfo.address : coApplicantInfo.address,
                  aptNumber: sameAddress ? userInfo.aptNumber : coApplicantInfo.aptNumber,
                  city: sameAddress ? userInfo.city : coApplicantInfo.city,
                  state: sameAddress ? userInfo.state : coApplicantInfo.state,
                  zip: sameAddress ? userInfo.zip : coApplicantInfo.zip,
                },
                company: response?.payload?.data?.company,
              }

              const coApplicantResponse = await dispatch(createCoApplicant(coApplicantData))
              if (coApplicantResponse.error) {
                setReqError(coApplicantResponse.payload || 'Failed to create co-applicant')
                coApplicantFailed = true
              } else {
                const coAppId = coApplicantResponse?.payload?._id
                setCoApplicantId(coAppId)

                const linkResponse = await dispatch(createContactLink({
                  primaryContact: newUserId,
                  linkedContact: coAppId,
                  linkType: 'co-applicant',
                  company: response?.payload?.data?.company,
                }))
                if (linkResponse.error) {
                  setReqError(linkResponse.payload || 'Failed to link co-applicant')
                  coApplicantFailed = true
                } else {
                  setCoApplicantLinked(true)
                }
              }
            }
          }

          if (!gatesOnLeadDetails) {
            getNewLeadDetails({ userId: newUserId })
            if (!coApplicantFailed) {
              setDisableTabs((prev) => ({ ...prev, creditSelect: false }))
              setSelectedTab("credit-select")
            }
          } else {
            const leadSaved = await getNewLeadDetails({ userId: newUserId })
            if (!coApplicantFailed && leadSaved) {
              setDisableTabs((prev) => ({ ...prev, creditSelect: false }))
              setSelectedTab("credit-select")
            }
          }
        }
      }
    } catch (error) {
      setReqError(error.message || 'Failed to create user')
    }

    triggerLoading(false)
  }


  const handleEditUser = async () => {
    pinKeepIdOnRefusal()
    triggerLoading(true)
    let coApplicantFailed = false
    const postHomeAddress = {
      address: userInfo.address,
      aptNumber: userInfo.aptNumber,
      city: userInfo.city,
      state: userInfo.state,
      zip: userInfo.zip,
    }
    const newNumber = userInfo?.primaryPhone?.replace(/\D/g, "")
    const newSecondaryNumber = userInfo?.secondaryPhone?.replace(/\D/g, "")
    const response = await dispatch(editContactById({
      userId,
      userData: {
        // WS #2219705 (QA 10-19): a cleared email on a PACK client is sent blank, and the server removes
        // the address. Left out, the old (often made-up) address stayed on file.
        ...userInfo,
        ...(emailOptional && !userInfo?.email ? { email: '' } : {}),
        primaryPhone: newNumber,
        secondaryPhone: newSecondaryNumber,
        address: postHomeAddress,
        dateOfBirth: dateToServer(userInfo.dateOfBirth),
        enrollmentDate: dateToServer(userInfo.enrollmentDate),
      }
    }))
    if (!keepIdOnRefusal) {
      setUserId(response?.payload?.data?._id)
    } else if (!response.error && response?.payload?.data?._id) {
      setUserId(response.payload.data._id)
    }
    if (response.error) {
      setReqError(response.payload ?? '')
    } else {
      if (hasCoApplicant) {
        if (coApplicantId) {
          const coApplicantData = {
            firstName: coApplicantInfo.firstName,
            lastName: coApplicantInfo.lastName,
            email: coApplicantInfo.email,
            primaryPhone: coApplicantInfo.primaryPhone?.replace(/\D/g, ''),
            ...(ssnWriteGuardRef.current && omitSsnUpdate(coApplicantInfo.ssNumber, coAppSsnWithheldRef.current)
              ? {}
              : { ssNumber: coApplicantInfo.ssNumber?.replace(/\D/g, '') }),
            dateOfBirth: dateToServer(coApplicantInfo.dateOfBirth),
            middleName: coApplicantInfo.middleName,
            gender: coApplicantInfo.gender,
            address: {
              address: sameAddress ? userInfo.address : coApplicantInfo.address,
              aptNumber: sameAddress ? userInfo.aptNumber : coApplicantInfo.aptNumber,
              city: sameAddress ? userInfo.city : coApplicantInfo.city,
              state: sameAddress ? userInfo.state : coApplicantInfo.state,
              zip: sameAddress ? userInfo.zip : coApplicantInfo.zip,
            },
          }
          const coRes = await dispatch(updateCoApplicant({ id: coApplicantId, data: coApplicantData }))
          if (coRes.error) {
            setReqError(coRes.payload || 'Failed to update co-applicant')
            coApplicantFailed = true
          }

          if (!coApplicantLinked) {
            const repairResponse = await dispatch(createContactLink({
              primaryContact: userId,
              linkedContact: coApplicantId,
              linkType: 'co-applicant',
              company: response?.payload?.data?.company,
            }))
            if (repairResponse.error) {
              setReqError(repairResponse.payload || 'Failed to link co-applicant')
              coApplicantFailed = true
            } else {
              setCoApplicantLinked(true)
            }
          }
        } else if (coApplicantMode === 'link' && linkedCoApplicantId) {
          setCoApplicantId(linkedCoApplicantId)
          const linkResponse = await dispatch(createContactLink({
            primaryContact: userId,
            linkedContact: linkedCoApplicantId,
            linkType: 'co-applicant',
            company: response?.payload?.data?.company,
          }))
          if (linkResponse.error) {
            setReqError(linkResponse.payload || 'Failed to link co-applicant')
            coApplicantFailed = true
          } else {
            setCoApplicantLinked(true)
          }
        } else if (coApplicantMode === 'create') {
          const coApplicantData = {
            firstName: coApplicantInfo.firstName,
            lastName: coApplicantInfo.lastName,
            email: coApplicantInfo.email,
            primaryPhone: coApplicantInfo.primaryPhone?.replace(/\D/g, ''),
            ssNumber: coApplicantInfo.ssNumber?.replace(/\D/g, ''),
            dateOfBirth: dateToServer(coApplicantInfo.dateOfBirth),
            middleName: coApplicantInfo.middleName,
            gender: coApplicantInfo.gender,
            address: {
              address: sameAddress ? userInfo.address : coApplicantInfo.address,
              aptNumber: sameAddress ? userInfo.aptNumber : coApplicantInfo.aptNumber,
              city: sameAddress ? userInfo.city : coApplicantInfo.city,
              state: sameAddress ? userInfo.state : coApplicantInfo.state,
              zip: sameAddress ? userInfo.zip : coApplicantInfo.zip,
            },
            company: response?.payload?.data?.company,
          }
          const coRes = await dispatch(createCoApplicant(coApplicantData))
          if (coRes.error) {
            setReqError(coRes.payload || 'Failed to create co-applicant')
            coApplicantFailed = true
          } else {
            const coAppId = coRes?.payload?._id
            setCoApplicantId(coAppId)
            const linkResponse = await dispatch(createContactLink({
              primaryContact: userId,
              linkedContact: coAppId,
              linkType: 'co-applicant',
              company: response?.payload?.data?.company,
            }))
            if (linkResponse.error) {
              setReqError(linkResponse.payload || 'Failed to link co-applicant')
              coApplicantFailed = true
            } else {
              setCoApplicantLinked(true)
            }
          }
        }
      }
      if (!coApplicantFailed) {
        showSnackBar("success", "Successfully updated")
        setSelectedTab("credit-select")
      }
    }
    triggerLoading(false)
  }

  const handleAddressSelect = useCallback((place) => {
    // Google fires place_changed with a stub place (just `name`, no address_components)
    // whenever the field is submitted without picking a suggestion — pressing Enter is
    // enough, so this is an ordinary user action. Treating it as a real selection wrote ""
    // over the address AND over any City/State/Zip the user had typed by hand, and all four
    // gate "Save & proceed", so the button silently re-disabled. Ignore it: leave whatever
    // they typed alone and let them correct it themselves.
    if (!place?.address_components?.length) return

    const streetNumber = place?.address_components?.find(c => c.types.includes("street_number"))?.long_name || ""
    const route = place?.address_components?.find(c => c.types.includes("route"))?.long_name || ""
    const fullAddress = streetNumber ? `${streetNumber} ${route}` : route

    setUserInfo(prev => ({
      ...prev,
      address: fullAddress,
      city: place?.address_components?.find(c => c.types.includes("locality"))?.long_name || "",
      state: place?.address_components?.find(c => c.types.includes("administrative_area_level_1"))?.short_name || "",
      zip: place?.address_components?.find(c => c.types.includes("postal_code"))?.long_name || ""
    }))
    setAddressInput(place?.formatted_address || "")
  }, [])

  const { ref: addressRef } = usePlacesWidget({
    apiKey: GOOGLE_MAPS_API_KEY,
    onPlaceSelected: handleAddressSelect,
    options: {
      types: ['address'],
      componentRestrictions: { country: 'us' },
    },
  })

  const getStates = async () => {
    try {
      const response = await dispatch(fetchStates({ searchQuery: '' }))
      setStates(response?.payload?.data)
    } catch (error) {
      showSnackBar("error", error)
    }
  }

  const getTimeZones = async () => {
    try {
      const response = await dispatch(fetchTimeZones({ searchQuery: '' }))
      setTimeZone(response?.payload?.data)
    } catch (error) {
      showSnackBar("error", error)
    }
  }

  useEffect(() => {
    !isCreate && getUserById()
    const fetch = async () => {
      await getStates()
      await getTimeZones()
    }
    fetch()
  }, [])


  return (
    <>
      <div className="bg-surface border border-divider shadow-lg rounded-2xl w-full max-w-[798px]">
        <div className="font-bold text-xl-20 pt-6 pl-12 pb-4">
          User Info
        </div>
        <div className="border-b border-divider mx-6" />
        <div className="flex px-12 pt-6 gap-4">
          <div className="flex-1">
            <Input
              placeholder="Enter First Name"
              label="First name *"
              value={userInfo.firstName}
              onChange={(e) => handleInputChange({ name: 'firstName', value: e.target.value })}
            />
          </div>
          <div className="flex-1">
            <Input
              placeholder="Enter Last Name"
              label="Last name *"
              value={userInfo.lastName}
              onChange={(e) => handleInputChange({ name: 'lastName', value: e.target.value })}
            />
          </div>
        </div>
        <div className="flex px-12 py-6 gap-4">
          <div className="flex-1">
            <Input
              placeholder="Enter Middle Name"
              label="Middle name"
              value={userInfo.middleName}
              onChange={(e) => handleInputChange({ name: 'middleName', value: e.target.value })}
            />
          </div>
          <div className="flex-1">
            <Input
              type="email"
              placeholder="Enter Email"
              label={emailOptional ? "Email (optional)" : "Email *"}
              value={userInfo.email}
              onChange={(e) => handleInputChange({ name: 'email', value: e.target.value })}
            />
          </div>
        </div>
        <div className="flex px-12 pb-6 gap-4">
          <div className="flex-1">
            <Input
              type="phone-number"
              placeholder="Enter Secondary Phone"
              label="Secondary phone"
              value={userInfo.secondaryPhone}
              onChange={(e) => handleInputChange({ name: 'secondaryPhone', value: e.target.value })}
            />
          </div>
          <div className="flex-1">
            <Input
              type="phone-number"
              placeholder="Enter Primary Phone"
              label="Primary phone *"
              value={userInfo.primaryPhone}
              onChange={(e) => handleInputChange({ name: 'primaryPhone', value: e.target.value })}
            />
          </div>
        </div>
        <div className="flex px-12 pb-6 gap-4">
          <Dropdown
            menuItems={[
              { label: 'Male', value: 'male', },
              { label: 'Female', value: 'female', }
            ]}
            label="Gender *"
            placeholder="Select Gender"
            value={userInfo.gender}
            type="text"
            name="gender"
            className="form-control w-full"
            required={true}
            setValue={(e) => handleInputChange({ name: 'gender', value: e })}
          />
          <Dropdown
            menuItems={timeZones?.map((timeZone) => ({
              label: timeZone.shortName,
              value: timeZone.shortName,
            }))}
            label="Time Zone"
            placeholder="Enter Time Zone"
            value={userInfo.timeZone}
            type="text"
            name="timeZones"
            className="form-control w-full"
            required={true}
            setValue={(e) => handleInputChange({ name: 'timeZone', value: e })}
          />
        </div>
        <div className="mx-12 mb-6 flex-1">
          <div className="ml-4 text-[14px] flex justify-start flex-col text-text-secondary mb-2">
            Lead Source
          </div>
          {/* WS #2296193: an office with its own list (Modern Financial) gets a plain dropdown of only that list. */}
          <Autocomplete
            freeSolo={!leadSourceOptions}
            options={leadSourceOptions
              ? leadSourceChoices(leadSourceOptions, userInfo.acquisitionSource)
              : ACQUISITION_SOURCE_OPTIONS.map(o => o.value)}
            value={userInfo.acquisitionSource || null}
            onChange={(_, newValue) => handleInputChange({ name: 'acquisitionSource', value: (newValue || '').slice(0, leadSourceMaxLength) })}
            onInputChange={(_, newValue, reason) => {
              if (reason === 'input' && !leadSourceOptions) handleInputChange({ name: 'acquisitionSource', value: (newValue || '').slice(0, leadSourceMaxLength) })
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={leadSourceOptions ? "Select Lead Source" : "Select or type Lead Source"}
                size="small"
                inputProps={{ ...params.inputProps, maxLength: leadSourceMaxLength }}
              />
            )}
            sx={{
              'fieldset': {
                border: '1px solid',
                borderRadius: '8px',
                height: '56px',
                marginTop: '5px',
                borderColor: 'var(--color-divider)',
                'legend': { display: 'none' },
              },
              '.MuiInputBase-root': { height: '56px' },
            }}
          />
        </div>
        <div className="mx-12 mb-6 flex-1">
          <Calendar
            optional={true}
            disableFuture={true}
            label="Date Of Birth (MM-DD-YYYY) *"
            value={userInfo.dateOfBirth}
            onChange={(e) => handleInputChange({ name: 'dateOfBirth', value: e })}
          />
        </div>
        {userId && userRole !== 'affiliate' && (
          <div className="mx-12 mb-12 flex-1">
            <Calendar
              disableFuture={true}
              label="Enrollment Date"
              value={userInfo.enrollmentDate}
              onChange={(e) => handleInputChange({ name: 'enrollmentDate', value: e })}
            />
          </div>
        )}
      </div>
      <div className="bg-surface border border-divider shadow-lg rounded-2xl w-full max-w-[798px] mt-6">
        <div className="font-bold text-xl-20 pt-6 pl-12 pb-4">
          Home Address
        </div>
        <div className="border-b border-divider mx-6" />
        <div className="flex px-12 pt-6 gap-4">
          <div className="w-full">
            <div className="flex justify-start flex-col text-text-secondary">
              <Input
                inputRef={addressRef}
                label="Address"
                placeholder="Enter your address..."
                value={addressInput}
                onChange={(e) => {
                  setAddressInput(e.target.value)
                  setUserInfo(prev => ({ ...prev, address: e.target.value }))
                }}
              />
            </div>
          </div>
          <div className="w-full">
            <Input
              placeholder="34"
              label="Suite/Apt"
              value={userInfo.aptNumber}
              onChange={(e) => {
                handleInputChange({ name: 'aptNumber', value: e.target.value })
              }}
            />
          </div>
        </div>
        <div className="flex gap-4">
        </div>
        <div className="flex px-12 pt-6 gap-4">
          <div className="flex-1">
            <Input
              label="City"
              placeholder="Enter City"
              value={userInfo.city}
              type="text"
              name="city"
              className="form-control"
              required={true}
              onChange={(e) => {
                handleInputChange({ name: 'city', value: e.target.value })
              }}
            />
          </div>
          <div className="flex-1">
            <Dropdown
              menuItems={states?.map((state) => ({
                label: `${state.shortName} - ${state.fullName}`,
                value: state.shortName,
              }))}
              label="State"
              placeholder="Enter State"
              value={userInfo.state}
              type="text"
              name="state"
              className="form-control"
              required={true}
              setValue={(e) => handleInputChange({ name: 'state', value: e })}
            />
          </div>
        </div>
        <div className="flex px-12 pt-6 pb-12 mr-4">
          <div className="w-1/2">
            <Input
              label="Zip"
              placeholder="Enter Zip"
              value={userInfo.zip}
              type="text"
              name="zip"
              className="form-control"
              required={true}
              onChange={(e) => {
                const value = e.target.value?.replace(/\D/g, '')
                handleInputChange({ name: 'zip', value })
              }}
            />
          </div>
        </div>
      </div>
      <div className="bg-surface border border-divider shadow-lg rounded-2xl w-full max-w-[798px] mt-6">
        <div className="flex justify-between items-center py-6 px-12">
          <div className="font-bold text-xl-20">Co-Applicant</div>
          <Switch
            id="hasCoApplicant"
            checked={hasCoApplicant}
            onChange={(val) => setHasCoApplicant(val)}
            label=""
            size="md"
          />
        </div>
        {hasCoApplicant && (
          <>
            <div className="border-b border-divider mx-6" />
            {coApplicantMode !== 'edit' && (
              <div className="flex px-12 pt-6 gap-4">
                <Button
                  size="sm"
                  type={coApplicantMode === 'create' ? "contained" : "outlined"}
                  onClick={() => {
                    setCoApplicantMode('create')
                    setLinkedCoApplicantId(null)
                    setCoApplicantInfo({
                      firstName: '', lastName: '', middleName: '', email: '',
                      primaryPhone: '', ssNumber: '', dateOfBirth: '', gender: '',
                      address: '', aptNumber: '', city: '', state: '', zip: '',
                    })
                  }}
                >
                  Create New
                </Button>
                <Button
                  size="sm"
                  type={coApplicantMode === 'link' ? "contained" : "outlined"}
                  onClick={() => {
                    setCoApplicantMode('link')
                    setLinkedCoApplicantId(null)
                    setCoApplicantInfo({
                      firstName: '', lastName: '', middleName: '', email: '',
                      primaryPhone: '', ssNumber: '', dateOfBirth: '', gender: '',
                      address: '', aptNumber: '', city: '', state: '', zip: '',
                    })
                  }}
                >
                  Link Existing
                </Button>
              </div>
            )}
            {coApplicantMode === 'link' && (
              <div className="px-12 pt-6 pb-6">
                <CoApplicantSearch
                  company={userCompany}
                  onSelect={handleSelectExistingCoApplicant}
                />
              </div>
            )}
            {(coApplicantMode === 'create' || coApplicantMode === 'edit' || linkedCoApplicantId) && (
              <>
                <div className="flex px-12 pt-6 gap-4">
                  <div className="flex-1">
                    <Input
                      placeholder="Enter First Name"
                      label="First name *"
                      value={coApplicantInfo.firstName}
                      readOnly={coApplicantMode === 'link' && !!linkedCoApplicantId}
                      onChange={(e) => handleCoApplicantInputChange({ name: 'firstName', value: e.target.value })}
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      placeholder="Enter Last Name"
                      label="Last name *"
                      value={coApplicantInfo.lastName}
                      readOnly={coApplicantMode === 'link' && !!linkedCoApplicantId}
                      onChange={(e) => handleCoApplicantInputChange({ name: 'lastName', value: e.target.value })}
                    />
                  </div>
                </div>
                <div className="flex px-12 py-6 gap-4">
                  <div className="flex-1">
                    <Input
                      placeholder="Enter Middle Name"
                      label="Middle name"
                      value={coApplicantInfo.middleName}
                      readOnly={coApplicantMode === 'link' && !!linkedCoApplicantId}
                      onChange={(e) => handleCoApplicantInputChange({ name: 'middleName', value: e.target.value })}
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      type="email"
                      placeholder="Enter Email"
                      label="Email"
                      value={coApplicantInfo.email}
                      readOnly={coApplicantMode === 'link' && !!linkedCoApplicantId}
                      onChange={(e) => handleCoApplicantInputChange({ name: 'email', value: e.target.value })}
                    />
                  </div>
                </div>
                <div className="flex px-12 pb-6 gap-4">
                  <div className="flex-1">
                    <Input
                      type="phone-number"
                      placeholder="Enter Phone"
                      label="Phone"
                      value={coApplicantInfo.primaryPhone}
                      readOnly={coApplicantMode === 'link' && !!linkedCoApplicantId}
                      onChange={(e) => handleCoApplicantInputChange({ name: 'primaryPhone', value: e.target.value })}
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      placeholder="Enter SSN"
                      label="SSN"
                      value={coApplicantInfo.ssNumber}
                      readOnly={coApplicantMode === 'link' && !!linkedCoApplicantId}
                      onChange={(e) => handleCoApplicantInputChange({ name: 'ssNumber', value: generateSSNumber(e.target.value) })}
                    />
                  </div>
                </div>
                <div className="flex px-12 pb-6 gap-4">
                  <Dropdown
                    menuItems={[
                      { label: 'Male', value: 'male' },
                      { label: 'Female', value: 'female' }
                    ]}
                    label="Gender"
                    placeholder="Select Gender"
                    value={coApplicantInfo.gender}
                    type="text"
                    name="gender"
                    className="form-control w-full"
                    disabled={coApplicantMode === 'link' && !!linkedCoApplicantId}
                    setValue={(e) => handleCoApplicantInputChange({ name: 'gender', value: e })}
                  />
                  <div className="flex-1" />
                </div>
                <div className="mx-12 mb-6">
                  <Calendar
                    optional={true}
                    disableFuture={true}
                    label="Date Of Birth (MM-DD-YYYY) *"
                    value={coApplicantInfo.dateOfBirth}
                    disabled={coApplicantMode === 'link' && !!linkedCoApplicantId}
                    onChange={(e) => handleCoApplicantInputChange({ name: 'dateOfBirth', value: e })}
                  />
                </div>
                {(coApplicantMode === 'create' || coApplicantMode === 'edit') && (
                  <div className="px-12 pb-4">
                    <Switch
                      id="sameAddress"
                      checked={sameAddress}
                      onChange={(val) => setSameAddress(val)}
                      label="Same address as applicant"
                      size="md"
                    />
                  </div>
                )}
                {(coApplicantMode === 'create' || coApplicantMode === 'edit') && !sameAddress && (
                  <CoApplicantAddressFields
                    coApplicantInfo={coApplicantInfo}
                    handleCoApplicantInputChange={handleCoApplicantInputChange}
                    setCoApplicantInfo={setCoApplicantInfo}
                    states={states}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
      <div className="mt-6 mb-6 flex justify-end space-x-4 w-full">
        <Button
          disabled={
            [
              userInfo.firstName,
              userInfo.lastName,
              ...(emailOptional ? [] : [userInfo.email]),
              userInfo.primaryPhone,
              userInfo.gender,
              userInfo.address,
              userInfo.city,
              userInfo.state,
              userInfo.zip,
            ]?.some(field => field === "") ||
            !userInfo.dateOfBirth ||
            emailBlocksSave({ email: userInfo.email, optional: emailOptional }) ||
            (hasCoApplicant && coApplicantMode === 'create' && [
              coApplicantInfo.firstName,
              coApplicantInfo.lastName,
              coApplicantInfo.dateOfBirth,
            ]?.some(field => !field)) ||
            (hasCoApplicant && coApplicantMode === 'link' && !linkedCoApplicantId)
          }
          width="min-w-[156px]"
          onClick={() => {
            if (!userId) {
              handleCreateContact()
            } else {
              handleEditUser()
            }
          }}
        >
          Save & proceed
        </Button>
      </div>
    </>
  )
}